import { useEffect, useMemo, useState } from 'react'

import { DxTradeTradingHour } from '@model/DxTrade.types'

export type TradingHourEventType = 'SESSION_OPEN' | 'SESSION_CLOSE'

export interface ParsedEvent {
  secondOfWeek: number
  eventType: TradingHourEventType
}

export const DAY_TO_INDEX: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
}

function parseEvent(e: DxTradeTradingHour): ParsedEvent | null {
  if (!e || !e.weekDay || !e.eventType) return null

  const match = e.weekDay.trim().match(/^([A-Za-z]+),\s*(\d{2}):(\d{2}):(\d{2})Z$/)
  if (!match) return null

  const dayName = match[1].toLowerCase()
  const h = Number(match[2])
  const m = Number(match[3])
  const s = Number(match[4])

  const dayIndex = DAY_TO_INDEX[dayName]
  if (dayIndex == null) return null

  const secondOfWeek = dayIndex * 24 * 3600 + h * 3600 + m * 60 + s
  const type =
    e.eventType === 'SESSION_OPEN' || e.eventType === 'SESSION_CLOSE'
      ? (e.eventType as TradingHourEventType)
      : null
  if (!type) return null
  return { secondOfWeek, eventType: type }
}

export function normalizeSchedule(events: DxTradeTradingHour[]): ParsedEvent[] {
  const parsed = events.map(parseEvent).filter((e): e is ParsedEvent => e != null)

  // Sort by time ascending; when times equal, CLOSE first, OPEN second
  const priority = (type: TradingHourEventType) => (type === 'SESSION_CLOSE' ? 0 : 1)
  parsed.sort((a, b) =>
    a.secondOfWeek !== b.secondOfWeek
      ? a.secondOfWeek - b.secondOfWeek
      : priority(a.eventType) - priority(b.eventType)
  )

  return parsed
}

function getNowSecondOfWeekUTC(now: Date): number {
  const dayIndex = now.getUTCDay()
  const h = now.getUTCHours()
  const m = now.getUTCMinutes()
  const s = now.getUTCSeconds()
  return dayIndex * 24 * 3600 + h * 3600 + m * 60 + s
}

export type TradingSessionStatus = 'open' | 'closed'

export interface TradingSessionResult {
  isOpen: boolean
  status: TradingSessionStatus
  nextChangeInSeconds?: number
  nextEvent?: { eventType: TradingHourEventType; weekSecond: number }
}

export function useTradingSessionStatus(
  tradingHours: DxTradeTradingHour[] | undefined | null,
  options?: { pollIntervalMs?: number }
): TradingSessionResult {
  const pollIntervalMs = options?.pollIntervalMs

  const [now, setNow] = useState<Date>(() => new Date())

  useEffect(() => {
    if (pollIntervalMs == null || pollIntervalMs <= 0) return
    const id = setInterval(() => setNow(new Date()), pollIntervalMs)
    return () => clearInterval(id)
  }, [pollIntervalMs])

  const result = useMemo<TradingSessionResult>(() => {
    if (!tradingHours || tradingHours.length === 0) {
      return { isOpen: false, status: 'closed' }
    }

    const schedule = normalizeSchedule(tradingHours)
    if (schedule.length === 0) {
      return { isOpen: false, status: 'closed' }
    }

    const nowSec = getNowSecondOfWeekUTC(now)

    let nextIndex = schedule.findIndex((e) => e.secondOfWeek > nowSec)
    if (nextIndex === -1) nextIndex = 0

    const prevIndex = nextIndex === 0 ? schedule.length - 1 : nextIndex - 1
    const prevEvent = schedule[prevIndex]
    const isOpen = prevEvent.eventType === 'SESSION_OPEN'

    const nextEvent = schedule[nextIndex]
    let delta = nextEvent.secondOfWeek - nowSec
    if (delta < 0) delta += 7 * 24 * 3600

    return {
      isOpen,
      status: isOpen ? 'open' : 'closed',
      nextChangeInSeconds: delta,
      nextEvent: { eventType: nextEvent.eventType, weekSecond: nextEvent.secondOfWeek },
    }
  }, [tradingHours, now])

  return result
}
